import type { AppData } from './types';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string; // emoji
  earned: boolean;
  earnedDate?: string;
}

export function checkAchievements(data: AppData): Achievement[] {
  const resetAt = data.settings.achievementsResetAt;
  const txns = resetAt
    ? data.transactions.filter(t => t.createdAt >= resetAt)
    : data.transactions;
  const bills = resetAt
    ? data.bills.filter(b => (b.lastPaidDate ?? b.dueDate) >= resetAt)
    : data.bills;
  const assets = resetAt
    ? data.assets.filter(a => a.createdAt >= resetAt)
    : data.assets;
  const savingsGoals = resetAt
    ? (data.savingsGoals ?? []).filter(g => g.createdAt >= resetAt)
    : (data.savingsGoals ?? []);
  const totalIncome = txns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const totalExpense = txns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
  const totalSaved = totalIncome - totalExpense;

  // No-spend days this month
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const daysWithExpense = new Set(
    txns.filter(t => t.type === 'expense' && t.date.startsWith(currentMonth)).map(t => t.date.slice(0, 10))
  );
  const dayOfMonth = now.getDate();
  let noSpendStreak = 0;
  for (let d = dayOfMonth; d >= 1; d--) {
    const ds = `${currentMonth}-${String(d).padStart(2, '0')}`;
    if (daysWithExpense.has(ds)) break;
    noSpendStreak++;
  }

  // Budget months under budget (filter by reset date)
  const budgets = resetAt
    ? data.budgets.filter(b => b.month >= resetAt.slice(0, 7))
    : data.budgets;
  const monthsUnderBudget = new Set<string>();
  const budgetsByMonth = new Map<string, typeof budgets>();
  for (const b of budgets) {
    const arr = budgetsByMonth.get(b.month) ?? [];
    arr.push(b);
    budgetsByMonth.set(b.month, arr);
  }
  for (const [month, budgets] of budgetsByMonth) {
    const monthTxns = txns.filter(t => t.date.startsWith(month) && t.type === 'expense');
    const allUnder = budgets.every(b => {
      const spent = monthTxns.filter(t => t.category === b.category).reduce((s, t) => s + t.amount, 0);
      return spent <= b.monthlyLimit;
    });
    if (allUnder && budgets.length > 0) monthsUnderBudget.add(month);
  }

  const achievements: Achievement[] = [
    {
      id: 'first_transaction',
      name: 'First Step',
      description: 'Log your first transaction',
      icon: '🚀',
      earned: txns.length >= 1,
    },
    {
      id: 'ten_transactions',
      name: 'Getting Started',
      description: 'Log 10 transactions',
      icon: '📝',
      earned: txns.length >= 10,
    },
    {
      id: 'hundred_transactions',
      name: 'Committed Tracker',
      description: 'Log 100 transactions',
      icon: '💪',
      earned: txns.length >= 100,
    },
    {
      id: 'saved_1k',
      name: 'First $1K',
      description: 'Reach $1,000 in net savings',
      icon: '💰',
      earned: totalSaved >= 1000,
    },
    {
      id: 'saved_5k',
      name: '$5K Club',
      description: 'Reach $5,000 in net savings',
      icon: '🏆',
      earned: totalSaved >= 5000,
    },
    {
      id: 'saved_10k',
      name: 'Five-Figure Saver',
      description: 'Reach $10,000 in net savings',
      icon: '⭐',
      earned: totalSaved >= 10000,
    },
    {
      id: 'budget_master',
      name: 'Budget Master',
      description: 'Stay under budget for 3 months',
      icon: '🎯',
      earned: monthsUnderBudget.size >= 3,
    },
    {
      id: 'no_spend_3',
      name: 'No-Spend Streak',
      description: '3 consecutive no-spend days',
      icon: '🔥',
      earned: noSpendStreak >= 3,
    },
    {
      id: 'no_spend_7',
      name: 'No-Spend Week',
      description: '7 consecutive no-spend days',
      icon: '🧊',
      earned: noSpendStreak >= 7,
    },
    {
      id: 'bill_ninja',
      name: 'Bill Ninja',
      description: 'Pay all your bills on time',
      icon: '🥷',
      earned: bills.length > 0 && bills.every(b => b.paid),
    },
    {
      id: 'savings_starter',
      name: 'Savings Starter',
      description: 'Create your first savings goal',
      icon: '🎯',
      earned: savingsGoals.length >= 1,
    },
    {
      id: 'goal_complete',
      name: 'Goal Crusher',
      description: 'Fully fund a savings goal',
      icon: '🏅',
      earned: savingsGoals.some(g => g.currentAmount >= g.targetAmount),
    },
    {
      id: 'asset_tracker',
      name: 'Asset Tracker',
      description: 'Add 5 items to your asset database',
      icon: '🏠',
      earned: assets.length >= 5,
    },
    {
      id: 'tax_ready',
      name: 'Tax Ready',
      description: 'Tag 10 expenses as tax-deductible',
      icon: '📋',
      earned: txns.filter(t => t.taxDeductible).length >= 10,
    },
  ];

  return achievements;
}
